import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Shield,
  LogOut,
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Star,
  DollarSign,
  GripVertical,
  Check,
} from "lucide-react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPlansPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem("slotmate_admin_token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/subscription-plans`, {
        headers: getAuthHeader(),
      });
      setPlans(response.data.plans);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Session expired");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("slotmate_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchPlans();
  }, [navigate, fetchPlans]);

  const handleLogout = () => {
    localStorage.removeItem("slotmate_admin_token");
    navigate("/admin");
  };

  const emptyPlan = {
    id: "",
    name: "",
    subtitle: "",
    price: 0,
    currency: "aud",
    interval: "month",
    popular: false,
    is_active: true,
    sort_order: plans.length + 1,
    features: [""],
    limits: { staff: -1, bookings_per_month: -1 },
  };

  const handleSave = async (plan, isNew = false) => {
    setSaving(true);
    try {
      const cleanPlan = {
        ...plan,
        price: parseFloat(plan.price),
        features: plan.features.filter((f) => f.trim() !== ""),
      };

      if (isNew) {
        await axios.post(`${API_URL}/admin/subscription-plans`, cleanPlan, {
          headers: { ...getAuthHeader(), "Content-Type": "application/json" },
        });
        toast.success("Plan created");
        setShowCreateForm(false);
      } else {
        await axios.put(
          `${API_URL}/admin/subscription-plans/${plan.id}`,
          cleanPlan,
          {
            headers: { ...getAuthHeader(), "Content-Type": "application/json" },
          },
        );
        toast.success("Plan updated");
        setEditingPlan(null);
      }
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm(`Delete the "${planId}" plan? This cannot be undone.`))
      return;
    try {
      await axios.delete(`${API_URL}/admin/subscription-plans/${planId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Plan deleted");
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white/60 animate-pulse">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/dashboard")}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">
                Subscription Plans
              </h1>
              <p className="text-white/40 text-xs">
                Manage pricing and features
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                setShowCreateForm(true);
                setEditingPlan(null);
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white"
              data-testid="create-plan-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Plan
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Create Form */}
        {showCreateForm && (
          <PlanForm
            plan={emptyPlan}
            isNew
            onSave={(p) => handleSave(p, true)}
            onCancel={() => setShowCreateForm(false)}
            saving={saving}
          />
        )}

        {/* Plans List */}
        <div className="space-y-4">
          {plans.map((plan) =>
            editingPlan === plan.id ? (
              <PlanForm
                key={plan.id}
                plan={plan}
                onSave={(p) => handleSave(p)}
                onCancel={() => setEditingPlan(null)}
                saving={saving}
              />
            ) : (
              <PlanCard
                key={plan.id}
                plan={plan}
                onEdit={() => setEditingPlan(plan.id)}
                onDelete={() => handleDelete(plan.id)}
              />
            ),
          )}
        </div>

        {plans.length === 0 && !showCreateForm && (
          <div className="text-center py-16 text-white/40">
            <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No subscription plans yet.</p>
            <p className="text-sm mt-1">
              Click "New Plan" to create your first plan.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

// ─── Plan Card (read-only) ──────────────────────────────────────────

const PlanCard = ({ plan, onEdit, onDelete }) => (
  <div
    className={`bg-white/5 backdrop-blur-xl rounded-xl border p-5 ${
      plan.popular ? "border-purple-500/50" : "border-white/10"
    }`}
    data-testid={`plan-card-${plan.id}`}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-white/20" />
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold text-lg">{plan.name}</h3>
              {plan.popular && (
                <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                  <Star className="w-3 h-3 mr-1" /> Popular
                </Badge>
              )}
              {!plan.is_active && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  Inactive
                </Badge>
              )}
            </div>
            <p className="text-white/40 text-sm mt-0.5">
              <span className="text-white font-bold text-xl">
                ${plan.price}
              </span>
              <span className="text-white/40">/{plan.interval}</span>
              <span className="ml-3 text-white/30">ID: {plan.id}</span>
            </p>
            {plan.subtitle && (
              <p className="text-white/30 text-xs mt-1 italic">
                {plan.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-white/40 hover:text-white hover:bg-white/10"
          data-testid={`edit-plan-${plan.id}`}
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-white/40 hover:text-red-400 hover:bg-red-500/10"
          data-testid={`delete-plan-${plan.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>

    {/* Features */}
    <div className="mt-3 flex flex-wrap gap-1.5">
      {plan.features.map((f, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 text-xs bg-white/5 text-white/50 border border-white/10 px-2 py-1 rounded-md"
        >
          <Check className="w-3 h-3 text-emerald-400" />
          {f}
        </span>
      ))}
    </div>

    {/* Limits */}
    <div className="mt-3 flex gap-4 text-xs text-white/30">
      <span>
        Staff: {plan.limits?.staff === -1 ? "Unlimited" : plan.limits?.staff}
      </span>
      <span>
        Bookings:{" "}
        {plan.limits?.bookings_per_month === -1
          ? "Unlimited"
          : plan.limits?.bookings_per_month + "/mo"}
      </span>
      <span>Order: {plan.sort_order}</span>
    </div>
  </div>
);

// ─── Plan Form (edit/create) ────────────────────────────────────────

const PlanForm = ({
  plan: initialPlan,
  isNew = false,
  onSave,
  onCancel,
  saving,
}) => {
  const [form, setForm] = useState({ ...initialPlan });

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const updateLimit = (field, value) =>
    setForm((prev) => ({
      ...prev,
      limits: { ...prev.limits, [field]: parseInt(value) || 0 },
    }));

  const addFeature = () =>
    setForm((prev) => ({ ...prev, features: [...prev.features, ""] }));
  const updateFeature = (idx, value) =>
    setForm((prev) => ({
      ...prev,
      features: prev.features.map((f, i) => (i === idx ? value : f)),
    }));
  const removeFeature = (idx) =>
    setForm((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== idx),
    }));

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-purple-500/30 p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">
          {isNew ? "Create New Plan" : `Edit: ${form.name}`}
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-white/40 hover:text-white"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Row 1: ID, Name, Price, Order */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-white/40 block mb-1">Plan ID</label>
            <Input
              value={form.id}
              onChange={(e) => updateField("id", e.target.value)}
              disabled={!isNew}
              placeholder="e.g. starter"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/30 disabled:opacity-50"
              data-testid="plan-id-input"
            />
          </div>
          <div>
            <label className="text-xs text-white/40 block mb-1">
              Display Name
            </label>
            <Input
              value={form.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="e.g. Starter"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
              data-testid="plan-name-input"
            />
          </div>
          <div>
            <label className="text-xs text-white/40 block mb-1">
              Price (AUD)
            </label>
            <Input
              type="number"
              step="0.01"
              value={form.price}
              onChange={(e) => updateField("price", e.target.value)}
              className="bg-white/10 border-white/20 text-white"
              data-testid="plan-price-input"
            />
          </div>
          <div>
            <label className="text-xs text-white/40 block mb-1">
              Sort Order
            </label>
            <Input
              type="number"
              value={form.sort_order}
              onChange={(e) =>
                updateField("sort_order", parseInt(e.target.value) || 0)
              }
              className="bg-white/10 border-white/20 text-white"
              data-testid="plan-sort-input"
            />
          </div>
        </div>

        {/* Row 2: Subtitle */}
        <div>
          <label className="text-xs text-white/40 block mb-1">
            Subtitle / Helper Text
          </label>
          <Input
            value={form.subtitle || ""}
            onChange={(e) => updateField("subtitle", e.target.value)}
            placeholder="e.g. For growing salons — shown below the price on landing & pricing pages"
            className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
            data-testid="plan-subtitle-input"
          />
        </div>

        {/* Row 2: Toggles + Limits */}
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
            <input
              type="checkbox"
              checked={form.popular}
              onChange={(e) => updateField("popular", e.target.checked)}
              className="rounded border-white/20"
            />
            <Star className="w-3.5 h-3.5" /> Most Popular
          </label>
          <label className="flex items-center gap-2 text-sm text-white/60 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField("is_active", e.target.checked)}
              className="rounded border-white/20"
            />
            Active
          </label>
          <div className="flex items-center gap-2 text-sm text-white/40">
            <span>Staff limit:</span>
            <Input
              type="number"
              value={form.limits?.staff ?? -1}
              onChange={(e) => updateLimit("staff", e.target.value)}
              className="w-20 h-8 bg-white/10 border-white/20 text-white text-xs"
            />
            <span className="text-white/30 text-xs">(-1 = unlimited)</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/40">
            <span>Bookings/mo:</span>
            <Input
              type="number"
              value={form.limits?.bookings_per_month ?? -1}
              onChange={(e) =>
                updateLimit("bookings_per_month", e.target.value)
              }
              className="w-20 h-8 bg-white/10 border-white/20 text-white text-xs"
            />
          </div>
        </div>

        {/* Features */}
        <div>
          <label className="text-xs text-white/40 block mb-2">Features</label>
          <div className="space-y-2">
            {form.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <Input
                  value={feature}
                  onChange={(e) => updateFeature(idx, e.target.value)}
                  placeholder="e.g. Unlimited Staff"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/30 text-sm h-8"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFeature(idx)}
                  className="text-white/30 hover:text-red-400 p-1 h-auto"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={addFeature}
              className="text-white/40 hover:text-white hover:bg-white/10 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Feature
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={onCancel}
            className="text-white/40 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave(form)}
            disabled={saving || !form.id || !form.name}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="save-plan-btn"
          >
            {saving ? (
              "Saving..."
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />{" "}
                {isNew ? "Create Plan" : "Save Changes"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminPlansPage;
